import React, { useState } from 'react';
import { userAppContext } from '../../context/Context';
import { validateEmailAcademic, validateEmailBusiness } from '../Support/ValidationLogic';
import PricePlanesAcademic from '../Pricings/PricePlanesAcademic';
import AdvancedPlanOnly from '../Pricings/AdvancedPlanOnly';
import Swal from 'sweetalert2';
import { useAcademicApplication } from '../../hooks/useAcademicApplication';
import AcademicApplicationModal from '../Academic/AcademicApplicationModal';

const UpgradePlan = () => {
  const { user, groups = [] } = userAppContext();
  const isTrial = groups?.includes('ROLE_TRIAL');
  const isAcademic = groups?.includes('ROLE_ACADEMIC');
  const isStandard = groups?.includes('ROLE_STANDARD');

  const [showModal, setShowModal] = useState(false);
  const [showModalAcademic, setShowModalAcademic] = useState(false);
  const [showModalBusiness, setShowModalBusiness] = useState(false);

  // Use custom hook for academic application logic
  const {
    trialType,
    academicStatus,
    loadingStatus,
    submitAcademicApplication,
    canSeePrices: canSeePricesFromHook,
    isCommercial: isCommercialFromHook,
  } = useAcademicApplication(user?.email);

  // Use tag-based logic from hook
  const isCommercialList = isCommercialFromHook();
  const canSeePricing = canSeePricesFromHook();

  const handleUpgradeClick = () => setShowModal(true);

  const handleCloseModal = () => {
    setShowModal(false);
    setShowModalAcademic(false);
    setShowModalBusiness(false);
  };

  const handleApplyToAcademicClick = () => setShowModalAcademic(true);

  const handleSubmit = e => {
    e.preventDefault();
    // No longer need to show logout modal - Stripe handles the upgrade flow
    setShowModal(false);
  };

  const handleConfirmBusiness = () => {
    setShowModalBusiness(true);
  };

  const handleBusinessSubmit = async e => {
    e.preventDefault();
    setShowModalBusiness(false);

    const messageInfo = {
      name: user?.['custom:name'] || 'Unknown',
      email: user?.email || 'Unknown',
      organization: user?.['custom:company'] || 'Unknown',
      role: user?.['custom:application_role'] || 'Unknown',
      message:
        "This user has completed a free trial. They have clicked the 'request pricing' button inside their account and would like to receive a quotation",
      subject: 'TRIAL EXPIRED: Request for commercial pricing',
    };

    try {
      const response = await fetch('https://p3xqewey7e.execute-api.eu-central-1.amazonaws.com/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(messageInfo),
      });
      if (response.ok) {
        Swal.fire({
          title: 'Your request has been sent',
          customClass: {
            confirmButton: 'btn-gradient-blue',
          },
        });
      } else {
        console.error('Error en la petición:', response.statusText);
        Swal.fire('Oops! Something went wrong.');
      }
    } catch (e) {
      console.error(e);
      Swal.fire('Error with your request. Try again later.');
    }
    setShowModalAcademic(false);
  };

  let buttonsToRender = null;

  if (loadingStatus) {
    buttonsToRender = <div>Loading...</div>;
  } else if (isTrial && trialType === 'academic') {
    if (academicStatus === 'pending') {
      buttonsToRender = (
        <div>
          <p className="text-muted">Your academic application is pending approval</p>
        </div>
      );
    } else if (academicStatus === 'approved') {
      buttonsToRender = (
        <div>
          <p className="text-muted">Your academic application has been approved</p>
        </div>
      );
    } else {
      buttonsToRender = (
        <div>
          <div className="text-right">
            <p className="mb-0">Do you want to upgrade your plan?</p>
            <button className="btn-gradient-pink">
              <a href="/Support" className="text-white">
                Contact us
              </a>
            </button>
          </div>
        </div>
      );
    }
  } else if (isTrial && trialType === 'commercial') {
    buttonsToRender = (
      <div>
        <button className="btn-gradient-blue">
          <a href="/book-a-demo?utm_source=web" target="_blank" className="text-white" rel="noreferrer">
            Schedule a demo
          </a>
        </button>
        <button className="btn-gradient-pink" onClick={handleConfirmBusiness}>
          Request Pricing
        </button>
      </div>
    );
  } else if (isTrial && !isCommercialList) {
    if (academicStatus === 'pending') {
      buttonsToRender = (
        <div>
          <p className="text-muted">Your academic application is pending approval</p>
          <button className="btn-gradient-pink" onClick={handleUpgradeClick}>
            Upgrade your Plan
          </button>
        </div>
      );
    } else if (academicStatus === 'approved') {
      buttonsToRender = (
        <div>
          <button className="btn-gradient-blue" onClick={handleApplyToAcademicClick}>
            Pay for Academic Plan
          </button>
          <button className="btn-gradient-pink" onClick={handleUpgradeClick}>
            Upgrade your Plan
          </button>
        </div>
      );
    } else {
      buttonsToRender = (
        <div>
          <button className="btn-gradient-blue" onClick={handleApplyToAcademicClick}>
            Apply for Free Academic Plan
          </button>
          <button className="btn-gradient-pink" onClick={handleUpgradeClick}>
            Upgrade your Plan
          </button>
        </div>
      );
    }
  } else if (isTrial && isCommercialList) {
    buttonsToRender = (
      <div>
        <button className="btn-gradient-blue">
          <a href="/book-a-demo?utm_source=web" target="_blank" className="text-white" rel="noreferrer">
            Schedule a demo
          </a>
        </button>
        <button className="btn-gradient-pink" onClick={handleBusinessSubmit}>
          Request Pricing
        </button>
      </div>
    );
  } else if (!isTrial && (isAcademic || isStandard)) {
    if (canSeePricing) {
      buttonsToRender = (
        <div>
          <button className="btn-gradient-pink" onClick={handleUpgradeClick}>
            Upgrade your Plan
          </button>
        </div>
      );
    } else {
      // User cannot see pricing - different behavior for academic vs commercial
      if (isAcademic) {
        // Academic users get contact button
        buttonsToRender = (
          <div className="grid justify-items-end">
            <button className="btn-gradient-pink mr-0">
              <a href="/Support" className="text-white">
                Contact Us
              </a>
            </button>
          </div>
        );
      } else {
        // Commercial users get request pricing
        buttonsToRender = (
          <div className="grid justify-items-end">
            <p className="mb-0">Do you want to upgrade your plan?</p>
            <button className="btn-gradient-pink mr-0" onClick={handleConfirmBusiness}>
              Request Pricing
            </button>
          </div>
        );
      }
    }
  } else {
    // Fallback - no condition matched
    console.warn('No button condition matched. User state:', {
      isTrial,
      isAcademic,
      isStandard,
      trialType,
      academicStatus,
    });
  }

  return (
    <div>
      {buttonsToRender}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="popup-upgrade">
            <button className="modal-close-btn" onClick={handleCloseModal}>
              x
            </button>

            <h3 className="text-center">Upgrade Your Plan</h3>

            {!canSeePricing ? (
              <div className="text-center p-4">
                <p className="mb-4">Contact us for pricing.</p>
                <button className="btn-gradient-pink">
                  <a href="/Support" className="text-white">
                    Contact us
                  </a>
                </button>
              </div>
            ) : isStandard ? (
              <AdvancedPlanOnly />
            ) : (
              <>
                <PricePlanesAcademic />
                <p className="text-muted text-center mt-3" style={{ fontSize: '0.85em' }}>
                  Not academic?{' '}
                  <a href="/Support" style={{ color: '#667eea', textDecoration: 'underline' }}>
                    Contact us
                  </a>
                  .
                </p>
              </>
            )}
          </div>
        </div>
      )}

      <AcademicApplicationModal
        isOpen={showModalAcademic}
        onClose={() => setShowModalAcademic(false)}
        onSubmit={submitAcademicApplication}
        academicStatus={academicStatus}
      />

      {/* Modal Business */}
      {showModalBusiness && (
        <div className="modal-overlay">
          <div className="RegisterPopup">
            <div className="apply-popup-container">
              <button className="modal-close-btn" onClick={handleCloseModal}>
                x
              </button>
              <>
                <h4 className="m-2">Are you sure you want to send a request for pricing?</h4>
                <p className="mt-4 text-base">Our team will get back to you shortly.</p>
                <button
                  onClick={handleBusinessSubmit}
                  className="btn-gradient-blue"
                  aria-label="submit-modal"
                >
                  Send Request
                </button>
                <button onClick={handleCloseModal} className="btn-gradient-pink" aria-label="cancel-modal">
                  Cancel
                </button>
              </>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpgradePlan;
